import { useMemo, useState } from "react";
import { When } from "react-if";
import { toDecimal } from "shared";

import { Modal } from "../../../common/Atoms/Modal";
import { CurrencyInput } from "../../../common/Atoms/CurrencyInput";
import { Button } from "../../../common/Atoms/Button";
import { Notification } from "../../../common/Atoms/Notification";
import { RadioCards } from "../../../common/Atoms/RadioCards";

interface OtherAmountModalProps {
  isOpen: boolean;
  onClose: () => void;
  otherAmount: number;
  setOtherAmount: (amount: number) => void;
  minAmountPercent: number;
  dueAmount: number;
  onPay: () => void;
  /** Shown as a picker when there is more than one */
  methods: { value: string; title: string }[];
  method: string;
  setMethod: (method: string) => void;
}

export function OtherAmountModal({
  isOpen,
  onClose,
  otherAmount,
  minAmountPercent,
  setOtherAmount,
  dueAmount,
  onPay,
  methods,
  method,
  setMethod,
}: OtherAmountModalProps) {
  const [error, setError] = useState(``);
  const shortCuts = useMemo(() => {
    const defaults = [
      10,
      25,
      50,
      75,
    ];

    return defaults.filter(percent => {
      return percent >= minAmountPercent;
    });
  }, [minAmountPercent]);

  function pay() {
    const minDue = Number(((minAmountPercent / 100) * dueAmount).toFixed(2));

    if (otherAmount < minDue) {
      setError(`Minimum payment amount is ${toDecimal(minDue).toFixed(2)}`);

      return;
    }

    if (otherAmount > dueAmount) {
      setError(`Payment amount cannot be greater than the due amount`);

      return;
    }

    setError(``);
    onPay();
  }

  return (
    <Modal
      open={ isOpen }
      setOpen={ onClose }
      title={ `` }
      width={ `md` }
    >
      <div>
        <When condition={ methods.length > 1 }>
          <div className={ `mb-4` }>
            <RadioCards
              label={ `Pay by` }
              name={ `paymentMethod` }
              options={ methods }
              selected={ method }
              onChange={ ({ value }) => setMethod(value) }
              cols={ methods.length }
            />
          </div>
        </When>
        <div className={ `w-full flex justify-between items-end space-x-2` }>
          <div className={ `w-full` }>
            <CurrencyInput
              label={ `Payment Amount (Minimum ${minAmountPercent}%)` }
              name={ `paymentAmount` }
              value={ otherAmount }
              onChange={ e =>  setOtherAmount(Number(e.value)) }
            />
          </div>

          <Button
            onClick={ pay }
          >
            { `Pay` }
          </Button>
        </div>

        <When condition={ shortCuts.length > 0 }>
          <div className={ `space-x-1` }>
            {
              shortCuts.map((percent, index) => {
                const amount = Number(((percent / 100) * dueAmount).toFixed(2));

                return (
                  <Button
                    key={ index }
                    color={ `gray` }
                    onClick={ () => setOtherAmount(amount) }
                    className={ `mt-2` }
                  >
                    { `${percent}% - ${toDecimal(amount).toFixed(2)}` }
                  </Button>
                );
              },
              ) }
          </div>
        </When>

        <When condition={ !!error }>
          <Notification
            type={ `error` }
            className={ `mt-2` }
          >
            { error }
          </Notification>
        </When>
      </div>
    </Modal>
  );
}

